import { useState } from "react";
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import useApiHelper from "../../hooks/useApiHelper";
import PriceFormat from "../../utils/priceFormat";
import classNames from "../../utils/classNames";
import {
  IPostcodePriceTestRequest,
  IPostcodePriceTestResponse,
  IPostcodePriceTestOwner,
  IPostcodeYearRuleTestResult,
  PostcodePriceSource,
} from "../../models/PostcodeGroup";
import { EngineCondition, BodyCondition } from "../../models/PriceSettings";

interface PostcodePriceTestModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const engineConditionOptions = [
  { id: EngineCondition.NoFault, label: "Good (No Fault)" },
  { id: EngineCondition.MinorFault, label: "Average (Minor Fault)" },
  { id: EngineCondition.MajorFault, label: "Bad (Major Fault)" },
];

const bodyConditionOptions = [
  { id: BodyCondition.Good, label: "Good" },
  { id: BodyCondition.Average, label: "Average" },
  { id: BodyCondition.HeavyDamage, label: "Bad (Heavy Damage)" },
];

/** The group or area, with a badge saying whether it was the one that set the price. */
function OwnerCard({
  title,
  owner,
  used,
  fellThroughTo,
}: {
  title: string;
  owner: IPostcodePriceTestOwner | null;
  used: boolean;
  fellThroughTo?: string;
}) {
  if (!owner)
    return (
      <div className="p-3 rounded-lg border border-dashed border-gray-300 bg-gray-50">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
        <p className="text-sm text-gray-500 mt-1">None set up for this postcode.</p>
      </div>
    );

  return (
    <div
      className={classNames(
        "p-3 rounded-lg border",
        used ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
        {used ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Used
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
            Not used
          </span>
        )}
      </div>
      <p className="text-sm font-semibold text-gray-800 mt-1">
        {owner.postcode}
        {owner.name ? ` — ${owner.name}` : ""}
      </p>
      <p className="text-sm text-gray-700">{PriceFormat(owner.pricePerTonne)} per tonne</p>
      <div className="mt-1 flex flex-wrap gap-1">
        {!owner.isActive && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
            Inactive
          </span>
        )}
        {owner.onlyGoodEngineCondition && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            Good engine only
          </span>
        )}
        {owner.onlyGoodBodyCondition && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            Good body only
          </span>
        )}
      </div>
      {fellThroughTo && <p className="text-xs text-gray-500 mt-2">{fellThroughTo}</p>}
    </div>
  );
}

function YearRuleCard({ rule }: { rule: IPostcodeYearRuleTestResult }) {
  return (
    <div
      className={classNames(
        "p-3 rounded-lg border",
        rule.applied ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {rule.applied ? (
              <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
            ) : (
              <XCircleIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            )}
            <span className="text-sm font-semibold text-gray-800">{rule.years}</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
              {rule.owner}
            </span>
            {!rule.isActive && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                Inactive
              </span>
            )}
            {/* A rule that fits the year but lost the tie-break is a different thing from one that
                never fitted, and it's the case people find hardest to see. */}
            {rule.matched && !rule.applied && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                Fits, but not used
              </span>
            )}
          </div>
        </div>
        <span
          className={classNames(
            "flex-shrink-0 text-sm font-semibold",
            rule.applied ? "text-green-700" : "text-gray-500"
          )}
        >
          {PriceFormat(rule.pricePerTonne)}/t
        </span>
      </div>

      {rule.reasons.length > 0 && (
        <ul className="mt-2 pt-2 border-t border-gray-200 space-y-1">
          {rule.reasons.map((reason, i) => (
            <li key={i} className="text-xs text-red-700 flex items-start gap-1.5">
              <span className="text-red-400 mt-0.5">&bull;</span>
              <span className="break-words">{reason}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function PostcodePriceTestModal({ open, setOpen }: PostcodePriceTestModalProps) {
  const { post } = useApiHelper();

  const [vrm, setVrm] = useState("");
  const [postcode, setPostcode] = useState("");
  const [engineCondition, setEngineCondition] = useState<EngineCondition>(EngineCondition.NoFault);
  const [bodyCondition, setBodyCondition] = useState<BodyCondition>(BodyCondition.Good);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<IPostcodePriceTestResponse | null>(null);

  const handleClose = () => {
    setOpen(false);
    setResult(null);
    setError(null);
    setVrm("");
    setPostcode("");
    setEngineCondition(EngineCondition.NoFault);
    setBodyCondition(BodyCondition.Good);
  };

  const handleTest = async () => {
    const trimmedVrm = vrm.trim().toUpperCase();
    const trimmedPostcode = postcode.trim().toUpperCase();

    if (!trimmedVrm) return setError("Please enter a VRM.");
    if (!trimmedPostcode) return setError("Please enter a postcode.");

    setError(null);
    setResult(null);
    setIsLoading(true);

    try {
      const response = await post<
        IPostcodePriceTestRequest,
        { success: boolean; data: IPostcodePriceTestResponse }
      >("/postcodePrice/test", {
        vrm: trimmedVrm,
        postcode: trimmedPostcode,
        engineCondition,
        bodyCondition,
      });

      if (response?.data) setResult(response.data);
      else setError("Unexpected response from server.");
    } catch (err: any) {
      setError(err?.message ?? "An error occurred while testing the postcode pricing.");
    } finally {
      setIsLoading(false);
    }
  };

  const priced = result?.vehicleFound && result.source !== PostcodePriceSource.None && result.blockers.length === 0;
  const yearRuleApplied = !!result?.appliedYearRuleId;

  return (
    <Modal width="max-w-2xl" open={open} setOpen={handleClose}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <div className="flex-shrink-0 p-2 rounded-lg bg-primary-50">
          <BeakerIcon className="h-5 w-5 text-primary-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Check a Reg and Postcode</h2>
          <p className="text-sm text-gray-500">
            See which group, area and year rule set the price per tonne — and why the others didn't.
          </p>
        </div>
      </div>

      <div className="mt-4 border-t border-gray-200 pt-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">VRM</label>
            <input
              type="text"
              value={vrm}
              onChange={(e) => setVrm(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && handleTest()}
              placeholder="e.g. AB12 CDE"
              maxLength={10}
              className="w-full text-sm text-gray-900 border border-gray-300 rounded-md shadow-sm px-3 py-2 uppercase placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
            <input
              type="text"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && handleTest()}
              placeholder="e.g. DN1 or DN1 1AB"
              maxLength={10}
              className="w-full text-sm text-gray-900 border border-gray-300 rounded-md shadow-sm px-3 py-2 uppercase placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Engine Condition</label>
            <select
              value={engineCondition}
              onChange={(e) => setEngineCondition(Number(e.target.value) as EngineCondition)}
              className="w-full text-sm text-gray-900 border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {engineConditionOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Body Condition</label>
            <select
              value={bodyCondition}
              onChange={(e) => setBodyCondition(Number(e.target.value) as BodyCondition)}
              className="w-full text-sm text-gray-900 border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {bodyConditionOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
            <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <button
          type="button"
          onClick={handleTest}
          disabled={isLoading}
          className="w-full rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:bg-gray-400 transition-colors"
        >
          {isLoading ? "Checking..." : "Check Pricing"}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="mt-5 border-t border-gray-200 pt-5 space-y-4">
          {!result.vehicleFound ? (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">{result.message ?? "Vehicle not found."}</p>
            </div>
          ) : (
            <>
              {/* What was looked up */}
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center gap-2 mb-2">
                  <MapPinIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  <p className="text-sm font-semibold text-gray-800">
                    {[result.make, result.model].filter(Boolean).join(" ") || result.vrm}
                    {result.year > 0 ? `, ${result.year}` : ""}
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-6">
                  <div className="flex items-baseline justify-between gap-2 py-1">
                    <span className="text-xs text-gray-500">Year rules matched on</span>
                    <span
                      className={classNames(
                        "text-xs font-mono",
                        result.year > 0 ? "text-gray-800" : "text-amber-600 italic"
                      )}
                    >
                      {result.year > 0 ? result.year : "no year on record"}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 py-1">
                    <span className="text-xs text-gray-500">Area / Group looked up</span>
                    <span className="text-xs font-mono text-gray-800">
                      {result.outcode} / {result.areaCode}
                    </span>
                  </div>
                </div>
              </div>

              {/* The answer */}
              {priced ? (
                <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                  <p className="text-xs font-semibold uppercase tracking-wide text-green-700">
                    Price per tonne used
                  </p>
                  <p className="text-2xl font-semibold text-green-800 mt-1">
                    {PriceFormat(result.effectivePricePerTonne)}
                  </p>
                  <p className="text-sm text-green-800 mt-1">
                    From the {result.source === PostcodePriceSource.Area ? "area" : "group"} price of{" "}
                    {PriceFormat(result.basePricePerTonne)}
                    {yearRuleApplied
                      ? `, moved to ${PriceFormat(result.effectivePricePerTonne)} by a year rule.`
                      : ", with no year rule applying."}
                  </p>
                  {result.thresholdPrice > 0 && (
                    <p className="text-xs text-green-700 mt-1">
                      Minimum accepted price: {PriceFormat(result.thresholdPrice)}.
                    </p>
                  )}
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-sm font-semibold text-amber-800">No price is given here</p>
                  <ul className="mt-2 space-y-1">
                    {result.blockers.map((blocker, i) => (
                      <li key={i} className="text-sm text-amber-800 flex items-start gap-1.5">
                        <span className="text-amber-500 mt-0.5">&bull;</span>
                        <span className="break-words">{blocker}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Any blockers that didn't stop a price still explain the route taken. */}
              {priced && result.blockers.length > 0 && (
                <ul className="space-y-1">
                  {result.blockers.map((blocker, i) => (
                    <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                      <span className="text-gray-400 mt-0.5">&bull;</span>
                      <span className="break-words">{blocker}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Group vs area */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <OwnerCard
                  title={`Group ${result.areaCode}`}
                  owner={result.group}
                  used={result.source === PostcodePriceSource.Group}
                  fellThroughTo={
                    result.source === PostcodePriceSource.Area
                      ? "An area price exists for this postcode, and an area always beats its group."
                      : undefined
                  }
                />
                <OwnerCard
                  title={`Area ${result.outcode}`}
                  owner={result.area}
                  used={result.source === PostcodePriceSource.Area}
                />
              </div>

              {/* Year rules */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Year rules ({result.yearRules.length})
                </p>
                {result.yearRules.length > 0 ? (
                  <div className="space-y-1.5">
                    {result.yearRules.map((rule) => (
                      <YearRuleCard key={rule.id} rule={rule} />
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    No year rules are set on this group or area, so the price per tonne is used as it
                    stands.
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
